import { Check, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AppIntegrationReview } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { Textarea } from '@/components/ui/textarea';

import { errorMessage, relativeTime } from './builderModel';

/**
 * App integrations waiting for approval: a Studio app's API its owner offered
 * to every organization from Manage → API. Until approved it is usable only in
 * its own organization; a rejection keeps it there, with the note shown to its
 * owner. Shown to integration approvers only.
 */
export function AppIntegrationReviews() {
  const [items, setItems] = useState<AppIntegrationReview[]>();
  const [error, setError] = useState<string>();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string>();

  const load = useCallback(() => {
    setError(undefined);
    adminClient
      .listAppIntegrationReviews('pending')
      .then((response) => setItems(response.items))
      .catch((failure) => setError(errorMessage(failure)));
  }, []);

  useEffect(load, [load]);

  const review = async (item: AppIntegrationReview, decision: 'approved' | 'rejected') => {
    const note = (notes[item.integrationId] ?? '').trim();
    if (decision === 'rejected' && !note) {
      toast.error('Say why it is rejected, so its owner can fix it.');
      return;
    }
    setBusy(item.integrationId);
    try {
      await adminClient.reviewAppIntegration(item.integrationId, { decision, note });
      toast.success(decision === 'approved' ? `${item.name} is listed for everyone` : `${item.name} stays in its organization`);
      setItems((current) => current?.filter((entry) => entry.integrationId !== item.integrationId));
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setBusy(undefined);
    }
  };

  if (error && !items) return <LoadError message={error} onRetry={load} className="mb-10 rounded-xl border border-border" />;
  if (!items) {
    return (
      <div className="mb-10 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading app integrations…
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="mb-10 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">App integrations waiting for approval</h2>
        <span className="text-xs text-muted-foreground">{items.length} waiting</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Studio apps whose owners offered their API to every organization. Approving lists it in every catalog; until then only its own organization can use it.
      </p>
      {items.map((item) => (
        <div key={item.integrationId} className="space-y-3 rounded-xl border border-border bg-card/40 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{item.name}</div>
              <div className="truncate font-mono text-[11px] text-muted-foreground">
                {item.capability} · {item.appName || 'Untitled app'} · {item.organizationId}
              </div>
              {item.description ? <p className="mt-1 text-xs text-muted-foreground">{item.description}</p> : null}
            </div>
            {item.approval ? (
              <span className="text-xs text-muted-foreground">
                {item.approval.requestedBy} · {relativeTime(item.approval.requestedAt)}
              </span>
            ) : null}
          </div>
          <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-lg border border-border text-xs">
            {item.operations.map((operation) => (
              <li key={`${operation.method} ${operation.path}`} className="flex items-center gap-3 px-3 py-1.5">
                <span className="w-14 shrink-0 font-mono font-semibold">{operation.method}</span>
                <code className="min-w-0 flex-1 truncate font-mono">{operation.path}</code>
                {operation.kind === 'write' ? <span className="text-warning">write</span> : null}
              </li>
            ))}
          </ul>
          <Textarea
            value={notes[item.integrationId] ?? ''}
            onChange={(event) => setNotes((current) => ({ ...current, [item.integrationId]: event.target.value }))}
            placeholder="Note for the owner (required to reject)"
            className="min-h-16 text-base md:text-sm"
            aria-label={`Note for ${item.name}`}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" disabled={busy === item.integrationId} onClick={() => void review(item, 'rejected')}>
              <X className="mr-1.5 h-3.5 w-3.5" />
              Reject
            </Button>
            <Button size="sm" disabled={busy === item.integrationId} onClick={() => void review(item, 'approved')}>
              <Check className="mr-1.5 h-3.5 w-3.5" />
              Approve for everyone
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
}
